import React, {useEffect, useState} from 'react'

import {Colors} from '../../../components/UI/index.js'
import LoadingPage from '../LoadingPage.js'
import NotFoundPage from '../NotFoundPage.js'
import api from './returnPortalApi.js'
import PortalLayout from './PortalLayout.js'
import SearchForm from './SearchForm.js'
import ReturnRequestFlow from './ReturnRequestFlow.js'
import ReturnParcelOverview from './ReturnParcelOverview.js'

/*
 * Return portal page: /retourportaal/:customerURL/:orderNumberOrTrackTrace?/:postalCode?
 *
 * After searching, the URL contains the order number and postal code, so refreshing the page (or coming back
 * from the payment page) loads the same parcel again. The backend returns either:
 * - {returnParcel}:                 a return is already made, show it (ReturnParcelOverview)
 * - {returnParcelRequest, options}: let the consumer request a new return (ReturnRequestFlow)
 */
export default function ReturnPortal({match, history}) {
    const {customerURL} = match.params

    const [portal, setPortal] = useState(null)
    const [portalError, setPortalError] = useState(false)
    const [search, setSearch] = useState({orderNumberOrTrackTrace: match.params.orderNumberOrTrackTrace || '', postalCode: match.params.postalCode || ''})
    const [result, setResult] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const color = portal?.color || Colors.brandColor

    // Coming back with the browser's back button (e.g. from the payment page) can restore a frozen copy of
    // this page from the back-forward cache, reload it so it shows the current state of the return
    useEffect(() => {
        const onPageShow = (event) => event.persisted && window.location.reload()
        window.addEventListener('pageshow', onPageShow)
        return () => window.removeEventListener('pageshow', onPageShow)
    }, [])

    useEffect(() => {
        document.title = 'Retourportaal'

        api.getPortal(customerURL)
            .then((portal) => {
                setPortal(portal)

                if (search.orderNumberOrTrackTrace && search.postalCode) {
                    find(search)
                }
            })
            .catch(() => setPortalError(true))
    }, [])

    const find = async (search, {silent = false} = {}) => {
        setSearch(search)
        setError('')

        if (!silent) {
            setLoading(true)
        }

        try {
            setResult(await api.find(customerURL, search.orderNumberOrTrackTrace, search.postalCode))
            history.replace(`/retourportaal/${customerURL}/${encodeURIComponent(search.orderNumberOrTrackTrace)}/${encodeURIComponent(search.postalCode)}${window.location.search}`)
        } catch (err) {
            if (silent) {
                console.error(err) // keep showing the current result, the next reload may succeed
            } else {
                setResult(null)
                setError(err.message)
            }
        } finally {
            setLoading(false)
        }
    }

    const pay = async () => {
        const {checkoutLink} = await api.createPayment(customerURL, search.orderNumberOrTrackTrace, search.postalCode)
        window.location = checkoutLink
    }

    if (portalError) {
        return <NotFoundPage />
    }

    if (!portal || (loading && !result)) {
        return <LoadingPage />
    }

    return (
        <PortalLayout portal={portal} color={color}>
            {!result &&
                <SearchForm color={color} initialSearch={search} loading={loading} error={error} onSearch={find} />
            }

            {result?.returnParcel &&
                <ReturnParcelOverview
                    portal={portal}
                    color={color}
                    returnParcel={result.returnParcel}
                    onReload={() => find(search, {silent: true})}
                    onPay={pay}
                />
            }

            {result?.returnParcelRequest &&
                <ReturnRequestFlow
                    portal={portal}
                    color={color}
                    initialRequest={result.returnParcelRequest}
                    options={result.options}
                    onSubmit={(returnParcelRequest) => api.submit(customerURL, returnParcelRequest)}
                    onCreated={(returnParcel) => setResult({returnParcel})}
                    onPay={pay}
                />
            }
        </PortalLayout>
    )
}
